import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchAIAgents } from '../../redux/ai/aiAgentSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import AgentSelector from '../../components/ai/AgentSelector';
import AgentCard from '../../pages/agents/components/AgentCard'; // AgentCardをインポート
import NewChatMessages from '../../components/chat/NewChatMessages';
import NewChatInput from '../../components/chat/NewChatInput';
import ChatSuggestions from '../../components/chat/ChatSuggestions';
import SuperAgentChat from '../../components/SuperAgent/SuperAgentChat';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../components/ui/select';
import { ResponsiveAppShell, TaskySelect, TaskyTopBar } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import '../../styles/markdown.scss';

// 商品選択機能関連のimport
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { clearSelectedProduct } from '../../redux/chat/chatSlice';
import SelectedProductBanner from '../../components/chat/SelectedProductBanner';

// カスタムフックとユーティリティ
import { useChatState } from './hooks/useChatState';
import { useChatEffects } from './hooks/useChatEffects';
import { useChatActions } from './hooks/useChatActions';
import { formatChats, formatAgents } from './utils/chatPageUtils';
import { ChatGenerationMode, Message } from '../../types/chat';
import chatService from '../../services/api/chatService';
import { OnboardingModal } from '../../components/onboarding';
import userService from '../../services/api/userService';
import { setUser } from '../../redux/auth/authSlice';
import { buildProductFocusPayload } from '../../utils/chatProductFocus';

/**
 * AIメッセージに対応するユーザーメッセージを取得する
 * @param messages メッセージ配列
 * @param aiMessageIndex AIメッセージのインデックス
 * @returns 対応するユーザーメッセージ、見つからない場合はnull
 */
const getCorrespondingUserMessage = (messages: Message[], aiMessageIndex: number): Message | null => {
  // AIメッセージより前のメッセージを逆順で検索
  for (let i = aiMessageIndex - 1; i >= 0; i--) {
    if (messages[i].sender_type === 'user') {
      return messages[i];
    }
  }
  return null;
};

const ChatPage: React.FC = () => {
  const { showToast } = useToast();
  // URLパラメータからチャットIDを取得
  const { chatId } = useParams<{ chatId: string }>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // 商品選択機能用のdispatchと状態
  const appDispatch = useAppDispatch();
  const selectedProduct = useAppSelector((state) => state.chat.selectedProduct);

  // AIチャットモードかどうかを判定
  const isAiChatMode = location.pathname === '/chat/ai';

  // Super Agentモードかどうかを判定
  const isSuperAgentMode = location.pathname === '/chat/super-agent';

  // Reduxからユーザー情報とAIエージェント情報を取得
  const { user } = useSelector((state: RootState) => state.auth);
  const { agents: reduxAgents, isLoading: isLoadingAgents } = useSelector((state: RootState) => state.aiAgent);

  // 状態管理
  const chatState = useChatState();

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Show onboarding modal if banners exist in sessionStorage (from onboarding flow)
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('onboarding_banners');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setShowOnboarding(true);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  const handleOnboardingComplete = useCallback(async () => {
    try {
      const response = await userService.completeOnboarding();
      if (response.success && response.user) {
        dispatch(setUser(response.user));
      }
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    } finally {
      setShowOnboarding(false);
    }
  }, [dispatch]);

  const handleOnboardingClose = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  // 汎用AIモデル選択用の状態（デフォルトはGPT-6 Sol）
  const [selectedAiModel, setSelectedAiModel] = useState<string>('gpt-6-sol');

  // AIチャットモード用のモデル選択処理
  const handleAiModelChange = (modelType: string) => {
    setSelectedAiModel(modelType);

    // 対応するエージェント名を定義（優先的にマッチさせるタイトル）
    const preferredTitleMap: { [key: string]: string } = {
      'gpt-6-astra': 'GPT-6 Astra',
      'gpt-6-sol': 'GPT-6 Sol',
      'gpt-6-luna': 'GPT-6 Luna',
      'claude-fable-5.1': 'Claude Fable 5.1',
      'claude-opus-5.5': 'Claude Opus 5.5',
      'claude-sonnet-5': 'Claude Sonnet 5',
      'gpt-5': 'GPT-5',
      'gpt-5-mini': 'GPT-5 Mini',
      'gpt-5-nano': 'GPT-5 Nano',
      'o3': 'o3',
      'chatgpt-4o-mini': '4o mini-high',
      'claude-sonnet-4': 'Claude Sonnet 4',
      'claude-opus-4': 'Claude Opus 4',
      'gemini-2.5-flash': 'Gemini 2.5 Flash',
      'gemini-2.5-pro': 'Gemini 2.5 Pro',
      'bytedance-seed-1.6': 'ByteDance Seed 1.6',
      'bytedance-seed-1.6-flash': 'ByteDance Seed 1.6 Flash'
    };

    const preferredTitle = preferredTitleMap[modelType];

    // 利用可能なエージェントをログ出力
    console.log('利用可能なエージェント:', chatState.agents.map(a => ({
      id: a.id,
      title: a.title,
      model_type: a.model_type
    })));

    // 1. model_typeとtitleが両方一致するエージェントを優先的に検索
    let targetAgent = chatState.agents.find(agent =>
      agent.model_type === modelType && agent.title === preferredTitle
    );

    if (targetAgent) {
      console.log(`AIモデル選択: ${modelType} -> ${preferredTitle} (ID: ${targetAgent.id})`);
      chatState.setSelectedAgent(targetAgent);
    } else {
      // 2. titleが一致しない場合は、モデルタイプのみで検索（フォールバック）
      const fallbackAgent = chatState.agents.find(agent => agent.model_type === modelType);
      if (fallbackAgent) {
        console.log(`AIモデル選択（フォールバック）: ${modelType} -> ${fallbackAgent.title} (ID: ${fallbackAgent.id})`);
        chatState.setSelectedAgent(fallbackAgent);
      } else {
        console.log(`エージェントが見つかりません: ${modelType}`);
      }
    }
  };

  // 新しいチャットボタンからの遷移を処理済みかどうかのフラグ
  const newChatProcessedRef = useRef<number | null>(null);

  // 「新しいチャット」ボタンからの遷移を監視
  useEffect(() => {
    // location.state?.newChat が存在する場合のみ、新しいチャットとして扱う
    const newChatTimestamp = location.state && (location.state as any).newChat;

    // まだ処理していない新しいチャット遷移の場合のみ実行
    if (isAiChatMode && newChatTimestamp && newChatProcessedRef.current !== newChatTimestamp && chatState.agents.length > 0) {
      // この遷移を処理済みとしてマーク
      newChatProcessedRef.current = newChatTimestamp;

      // メッセージをクリア
      chatState.setMessages([]);
      chatState.setIsNewChat(true);

      // selectedAiModelをデフォルトの'gpt-6-sol'にリセット
      setSelectedAiModel('gpt-6-sol');

      // 直接GPT-6 Solエージェントを探して設定
      const defaultAgent = chatState.agents.find(agent =>
        agent.model_type === 'gpt-6-sol' && agent.title === 'GPT-6 Sol'
      ) || chatState.agents.find(agent => agent.model_type === 'gpt-6-sol');

      if (defaultAgent) {
        chatState.setSelectedAgent(defaultAgent);
      } else {
        chatState.setSelectedAgent(null);
      }
    }
  }, [isAiChatMode, location.state, chatState.agents, chatState.setMessages, chatState.setIsNewChat, chatState.setSelectedAgent, setSelectedAiModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // AIチャットモード時にデフォルトエージェントを設定
  useEffect(() => {
    if (isAiChatMode && chatState.agents.length > 0) {
      // 現在選択されているエージェントがリストに存在し、かつ正しいエージェントか確認
      const currentAgentExistsInList = chatState.selectedAgent &&
        chatState.agents.some(a =>
          a.id === chatState.selectedAgent?.id &&
          a.model_type === chatState.selectedAgent?.model_type &&
          a.title === chatState.selectedAgent?.title
        );

      // エージェントが未選択、またはリストに存在しない場合は選択
      if (!currentAgentExistsInList) {
        // モデル名マッピング（優先的にマッチさせるタイトル）
        const preferredTitleMap: { [key: string]: string } = {
          'gpt-6-astra': 'GPT-6 Astra',
          'gpt-6-sol': 'GPT-6 Sol',
          'gpt-6-luna': 'GPT-6 Luna',
          'claude-fable-5.1': 'Claude Fable 5.1',
          'claude-opus-5.5': 'Claude Opus 5.5',
          'claude-sonnet-5': 'Claude Sonnet 5',
          'gpt-5': 'GPT-5',
          'gpt-5-mini': 'GPT-5 Mini',
          'gpt-5-nano': 'GPT-5 Nano',
          'o3': 'o3',
          'chatgpt-4o-mini': '4o mini-high',
          'claude-sonnet-4': 'Claude Sonnet 4',
          'claude-opus-4': 'Claude Opus 4',
          'gemini-2.5-flash': 'Gemini 2.5 Flash',
          'gemini-2.5-pro': 'Gemini 2.5 Pro',
          'bytedance-seed-1.6': 'ByteDance Seed 1.6',
          'bytedance-seed-1.6-flash': 'ByteDance Seed 1.6 Flash'
        };

        // 選択されたモデルに対応するエージェントを設定
        // 1. model_typeとtitleが両方一致するものを優先
        const preferredTitle = preferredTitleMap[selectedAiModel];
        let targetAgent = chatState.agents.find(agent =>
          agent.model_type === selectedAiModel && agent.title === preferredTitle
        );

        // 2. titleが一致しない場合はmodel_typeのみで検索
        if (!targetAgent) {
          targetAgent = chatState.agents.find(agent =>
            agent.model_type === selectedAiModel
          );
        }

        if (targetAgent) {
          console.log(`AIチャットモード: ${selectedAiModel} エージェントを設定 (${targetAgent.title})`);
          chatState.setSelectedAgent(targetAgent);
        } else {
          // GPT-6 Solを優先的に探す（タイトルが正確に一致するものを優先）
          const solAgent = chatState.agents.find(agent =>
            agent.model_type === 'gpt-6-sol' && agent.title === 'GPT-6 Sol'
          ) || chatState.agents.find(agent => agent.model_type === 'gpt-6-sol');

          const fallbackAgent = solAgent || chatState.agents[0];
          if (fallbackAgent) {
            console.log('AIチャットモード: フォールバックエージェントを設定:', fallbackAgent.title, fallbackAgent.model_type);
            chatState.setSelectedAgent(fallbackAgent);
            setSelectedAiModel(fallbackAgent.model_type || 'gpt-6-sol');
          }
        }
      }
    }
  }, [isAiChatMode, chatState.agents, chatState.selectedAgent, selectedAiModel, chatState.setSelectedAgent, setSelectedAiModel]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // エージェント変換のメモ化
  const convertAgentsWithRef = useRef(false);

  // Reduxからエージェント一覧を取得
  useEffect(() => {
    if (user) {
      console.log('Reduxからエージェント一覧を取得します', { isAiChatMode });
      // モードが変わったら変換フラグをリセット
      convertAgentsWithRef.current = false;
      // AIチャットモードの場合は専用のエンドポイントから取得
      if (isAiChatMode) {
        // AIチャットモード専用のエージェントを直接取得
        const fetchChatModelAgents = async () => {
          try {
            const aiAgentService = await import('../../services/api/aiAgentService').then(
              module => module.default
            );
            let chatModelAgents = await aiAgentService.getChatModelAgents();
            console.log('AIチャットモード用エージェント取得完了:', chatModelAgents.length);
            
            // GPT-6関連を優先的に配置するようにソート
            chatModelAgents = chatModelAgents.sort((a, b) => {
              const priority = {
                'gpt-6-astra': 0.5,
                'gpt-6-sol': 1,
                'gpt-6-luna': 3,
                'claude-fable-5.1': 3.1,
                'claude-opus-5.5': 3.2,
                'claude-sonnet-5': 3.3,
                'gpt-5': 4,
                'gpt-5-mini': 5,
                'gpt-5-nano': 6,
                'o3': 7,
                'chatgpt-4o-mini': 8,
                'claude-sonnet-4': 9,
                'claude-opus-4': 10,
                'gemini-2.5-flash': 11,
                'gemini-2.5-pro': 12,
                'bytedance-seed-1.6': 13,
                'bytedance-seed-1.6-flash': 14
              };

              // 優先タイトルマッピング（正確なタイトルを優先）
              const preferredTitles = {
                'gpt-6-astra': 'GPT-6 Astra',
                'gpt-6-sol': 'GPT-6 Sol',
                'gpt-6-luna': 'GPT-6 Luna',
                'claude-fable-5.1': 'Claude Fable 5.1',
                'claude-opus-5.5': 'Claude Opus 5.5',
                'claude-sonnet-5': 'Claude Sonnet 5',
                'gpt-5': 'GPT-5',
                'gpt-5-mini': 'GPT-5 Mini',
                'gpt-5-nano': 'GPT-5 Nano',
                'o3': 'o3',
                'chatgpt-4o-mini': '4o mini-high',
                'claude-sonnet-4': 'Claude Sonnet 4',
                'claude-opus-4': 'Claude Opus 4',
                'gemini-2.5-flash': 'Gemini 2.5 Flash',
                'gemini-2.5-pro': 'Gemini 2.5 Pro',
                'bytedance-seed-1.6': 'ByteDance Seed 1.6',
                'bytedance-seed-1.6-flash': 'ByteDance Seed 1.6 Flash'
              };

              const aPriority = priority[a.model_type] || 999;
              const bPriority = priority[b.model_type] || 999;

              // 優先度が同じ場合は、優先タイトルと一致するものを先に
              if (aPriority === bPriority) {
                const aIsPreferred = a.title === preferredTitles[a.model_type];
                const bIsPreferred = b.title === preferredTitles[b.model_type];
                if (aIsPreferred && !bIsPreferred) return -1;
                if (!aIsPreferred && bIsPreferred) return 1;
                return 0;
              }

              return aPriority - bPriority;
            });
            
            // 通常のエージェントも取得（サイドバー表示用）
            dispatch(fetchAIAgents(false));
            
            // AIAgentをAgentWithIconに変換
            const agentsWithIcons = chatModelAgents.map(agent => {
              console.log('エージェント変換:', agent.id, agent.title, 'model_type:', agent.model_type);
              const modelIcons: {[key: string]: string} = {
                'gpt-6-astra': '🤖',
                'gpt-6-sol': '🤖',
                'gpt-6-luna': '🤖',
                'gpt-5': '🤖',
                'gpt-5-mini': '🤖',
                'gpt-5-nano': '🤖',
                'chatgpt': '🤖',
                'o3': '🤖',
                'chatgpt-4o-mini': '🤖',
                'claude': '🧠',
                'claude-fable-5.1': '🧠',
                'claude-opus-5.5': '🧠',
                'claude-sonnet-5': '🧠',
                'claude-sonnet-4': '🧠',
                'claude-opus-4': '🧠',
                'gemini': '✨',
                'gemini-2.5-flash': '✨',
                'gemini-2.5-pro': '✨',
                'bytedance-seed-1.6': '🔥',
                'bytedance-seed-1.6-flash': '🔥'
              };
              
              return {
                ...agent,
                name: agent.title || 'Unknown Agent',
                avatar: `https://via.placeholder.com/40?text=${agent.title?.substring(0, 2) || 'AI'}`,
                capabilities: [],
                icon: modelIcons[agent.model_type || 'chatgpt'] || '🤖'
              };
            });
            
            // チャットモード用エージェントをセット
            chatState.setAgents(agentsWithIcons);
            chatState.setIsLoadingAgents(false);
          } catch (error) {
            console.error('AIチャットモード用エージェント取得エラー:', error);
          }
        };
        
        fetchChatModelAgents();
      } else {
        // 通常モードの場合は通常のエージェントを取得
        dispatch(fetchAIAgents(false));
      }
    }
  }, [dispatch, user, isAiChatMode, chatState.setAgents, chatState.setIsLoadingAgents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reduxのエージェント情報をローカルステートに反映 - 厳密に一度だけ実行
  useEffect(() => {
    // AIチャットモードではこの処理をスキップ（専用の取得処理があるため）
    if (isAiChatMode) return;
    
    // エージェントデータがあり、まだロード中でなく、まだ変換を行っていない場合のみ実行
    if (reduxAgents.length > 0 && !isLoadingAgents && !convertAgentsWithRef.current) {
      console.log('Reduxのエージェント情報をローカルステートに反映します - 完全に一度だけ実行');
      
      // 変換済みフラグを設定
      convertAgentsWithRef.current = true;
      
      // AIAgentをAgentWithIconに変換
      const agentsWithIcons = reduxAgents.map(agent => {
        console.log('エージェント変換:', agent.id, agent.title, 'model_type:', agent.model_type);
        const modelIcons: {[key: string]: string} = {
          'gpt-6-astra': '🤖',
          'gpt-6-sol': '🤖',
          'gpt-6-luna': '🤖',
          'gpt-5': '🤖',
          'gpt-5-mini': '🤖',
          'gpt-5-nano': '🤖',
          'chatgpt': '🤖',
          'o3': '🤖',
          'chatgpt-4o-mini': '🤖',
          'claude': '🧠',
          'claude-fable-5.1': '🧠',
          'claude-opus-5.5': '🧠',
          'claude-sonnet-5': '🧠',
          'claude-sonnet-4': '🧠',
          'claude-opus-4': '🧠',
          'gemini': '✨',
          'gemini-2.5-flash': '✨',
          'gemini-2.5-pro': '✨',
          'bytedance-seed-1.6': '🔥',
          'bytedance-seed-1.6-flash': '🔥'
        };
        
        return {
          ...agent,
          name: agent.title || 'Unknown Agent',
          avatar: `https://via.placeholder.com/40?text=${agent.title?.substring(0, 2) || 'AI'}`,
          capabilities: [],
          icon: modelIcons[agent.model_type || 'chatgpt'] || '🤖'
        };
      });
      
      // 一度だけセット
      chatState.setAgents(agentsWithIcons);
      chatState.setIsLoadingAgents(false);
    }
  }, [reduxAgents.length, isLoadingAgents, isAiChatMode, chatState.setAgents, chatState.setIsLoadingAgents]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 副作用（useEffect）
  useChatEffects(chatId, {
    ...chatState,
    // ThinkingProcess関連を明示的に追加
    showThinkingProcess: chatState.showThinkingProcess,
    setShowThinkingProcess: chatState.setShowThinkingProcess,
    setThinkingMessage: chatState.setThinkingMessage
  }, user);

  // アクション
  const chatActions = useChatActions(
    {
      isNewChat: chatState.isNewChat,
      chatId,
      selectedAgent: chatState.selectedAgent,
      messages: chatState.messages,
      setMessages: chatState.setMessages,
      messageInput: chatState.messageInput,
      setMessageInput: chatState.setMessageInput,
      addMessage: chatState.addMessage,
      setIsLoading: chatState.setIsLoading,
      setTypingIndicator: chatState.setTypingIndicator,
      chats: chatState.chats,
      setChats: chatState.setChats,
      agents: chatState.agents,
      orgTokenInfo: chatState.orgTokenInfo,
      setSuggestions: chatState.setSuggestions,
      // ThinkingProcess関連
      showThinkingProcess: chatState.showThinkingProcess,
      setShowThinkingProcess: chatState.setShowThinkingProcess,
      setThinkingMessage: chatState.setThinkingMessage,
      setCurrentChatId: chatState.setCurrentChatId,
      // GPT-5 AIのthinking（reasoning summary）内容
      aiThinkingContent: chatState.aiThinkingContent,
      setAiThinkingContent: chatState.setAiThinkingContent
    },
    user
  );

  // 検索進行状況の管理

  // チャット情報を整形
  const formattedChats = formatChats(chatState.chats);

  // エージェント情報を整形
  const formattedAgents = formatAgents(chatState.agents);
  
  // サイドバー用のエージェント
  // AIチャットモードでもReduxから取得した通常のエージェントを使用
  const sidebarAgents = (() => {
    if (isAiChatMode) {
      // ReduxのエージェントをAgentWithIconに変換
      const modelIcons: {[key: string]: string} = {
        'gpt-6-astra': '🤖',
        'gpt-6-sol': '🤖',
        'gpt-6-luna': '🤖',
        'gpt-5': '🤖',
        'gpt-5-mini': '🤖',
        'gpt-5-nano': '🤖',
        'chatgpt': '🤖',
        'o3': '🤖',
        'chatgpt-4o-mini': '🤖',
        'claude': '🧠',
        'claude-fable-5.1': '🧠',
        'claude-opus-5.5': '🧠',
        'claude-sonnet-5': '🧠',
        'claude-sonnet-4': '🧠',
        'claude-opus-4': '🧠',
        'gemini': '✨',
        'gemini-2.5-flash': '✨',
        'gemini-2.5-pro': '✨',
        'bytedance-seed-1.6': '🔥',
        'bytedance-seed-1.6-flash': '🔥'
      };
      
      const convertedAgents = reduxAgents
        .filter(agent => (agent as any).is_visible_in_sidebar !== false)
        .map(agent => ({
          ...agent,
          name: agent.title || 'Unknown Agent',
          avatar: `https://via.placeholder.com/40?text=${agent.title?.substring(0, 2) || 'AI'}`,
          capabilities: [],
          icon: modelIcons[agent.model_type || 'chatgpt'] || '🤖'
        }));
      
      return formatAgents(convertedAgents);
    } else {
      return formatAgents(chatState.agents.filter(agent => 
        agent.is_visible_in_sidebar !== false
      ));
    }
  })();
  

  // エージェント選択時の処理
  const handleChangeAgent = (agentId: number) => {
    chatActions.handleChangeAgent(
      agentId,
      chatState.agents,
      chatState.setSelectedAgent,
      chatState.setAgentSelectorVisible // カード選択時はセレクタを非表示にする
    );
    // エージェントが選択されたら、カードリストを非表示にするために isNewChat を false にするなどの工夫が必要
    // ここでは selectedAgent がセットされることで後続の表示が変わることを期待
    if (chatState.isNewChat) {
      // 新規チャットでエージェントが選択されたら、エージェント選択UIを非表示にする
      // chatState.setIsNewChat(false); // これは副作用を引き起こす可能性があるので注意
    }
  };

  // メッセージ再生成の処理
  const handleRegenerateMessage = async (aiMessageId: number) => {
    console.log('🔄 再生成開始:', aiMessageId);

    // 1. AIメッセージのインデックスを取得
    const aiMessageIndex = chatState.messages.findIndex(m => m.id === aiMessageId);
    if (aiMessageIndex === -1) {
      showToast('メッセージが見つかりません', 'error');
      return;
    }

    // 2. 対応するユーザーメッセージを特定
    const userMessage = getCorrespondingUserMessage(chatState.messages, aiMessageIndex);
    if (!userMessage) {
      showToast('対応するユーザーメッセージが見つかりません', 'error');
      return;
    }

    console.log('📝 対応するユーザーメッセージ:', userMessage.content.substring(0, 50) + '...');

    // 3. ローディング状態と再生成状態を設定
    chatState.setIsLoading(true);
    chatState.setTypingIndicator(true);
    chatState.setIsRegenerating(true);
    chatState.setRegeneratingMessageId(aiMessageId);

    try {
      // 4. メッセージを送信（既存のAPI呼び出しを再利用）
      const response = await chatService.sendMessage({
        chat_id: Number(chatId),
        content: userMessage.content,
        has_attachments: userMessage.attachments && userMessage.attachments.length > 0,
        uploaded_files: userMessage.uploaded_files,
        enable_search: false,
        product_id: undefined
      });

      console.log('✅ メッセージ送信成功:', response.ai_message_id);

      // 5. プレースホルダーを追加
      const aiMessagePlaceholder: Message = {
        id: response.ai_message_id,
        content: '',
        sender_type: 'ai',
        created_at: new Date().toISOString(),
        status: 'processing'
      };

      // 6. 既存のAIメッセージを新しいプレースホルダーで置き換え
      chatState.setMessages((prevMessages: Message[]) => {
        const newMessages = [...prevMessages];
        newMessages[aiMessageIndex] = aiMessagePlaceholder;
        return newMessages;
      });

      // 7. ポーリングでAI応答を待つ
      const pollForResponse = async () => {
        try {
          const status = await chatService.getMessageStatus(response.ai_message_id);

          if (status.status === 'completed') {
            console.log('✅ AI応答が完了');

            // AI応答をUI更新
            const aiResponse: Message = {
              id: response.ai_message_id,
              content: status.content || '',
              sender_type: 'ai',
              created_at: status.created_at,
              suggestions: status.suggestions,
              status: 'completed'
            };

            // メッセージを更新
            chatState.setMessages((prevMessages: Message[]) => {
              const newMessages = [...prevMessages];
              newMessages[aiMessageIndex] = aiResponse;
              return newMessages;
            });

            // ロード状態とタイピングインジケーター、再生成状態を解除
            chatState.setIsLoading(false);
            chatState.setTypingIndicator(false);
            chatState.setIsRegenerating(false);
            chatState.setRegeneratingMessageId(null);
          } else if (status.status === 'failed') {
            showToast('AI応答の処理に失敗しました。', 'error');
            chatState.setIsLoading(false);
            chatState.setTypingIndicator(false);
            chatState.setIsRegenerating(false);
            chatState.setRegeneratingMessageId(null);
          } else {
            // まだ処理中の場合、2秒後に再確認
            setTimeout(pollForResponse, 2000);
          }
        } catch (error) {
          console.error('AI応答の取得に失敗:', error);
          showToast('AI応答の取得に失敗しました。', 'error');
          chatState.setIsLoading(false);
          chatState.setTypingIndicator(false);
          chatState.setIsRegenerating(false);
          chatState.setRegeneratingMessageId(null);
        }
      };

      // 2秒後にポーリング開始
      setTimeout(pollForResponse, 2000);
    } catch (error) {
      console.error('再生成エラー:', error);
      showToast('再生成に失敗しました。もう一度お試しください。', 'error');
      chatState.setIsLoading(false);
      chatState.setTypingIndicator(false);
      chatState.setIsRegenerating(false);
      chatState.setRegeneratingMessageId(null);
    }
  };

  // 新規チャット時で、まだエージェントが選択されていない場合にカードリストを表示
  const shouldShowAgentCards = chatState.isNewChat && !chatState.selectedAgent;

  // 中央配置のウェルカム画面を表示する条件:
  // - AIチャットモード（/chat/ai）でメッセージがない場合 → 中央配置
  // - エージェント選択時（/agentsから選択）はチャット画面の見た目（入力欄が下部）を使用
  const shouldShowSelectedAgentWelcome = chatState.messages.length === 0 &&
    isAiChatMode && (!chatId || chatState.isNewChat);

  // quick_repliesをパース（JSON文字列の場合があるため）
  const parseQuickReplies = (replies: any): string[] => {
    if (!replies) return [];
    if (Array.isArray(replies)) return replies;
    if (typeof replies === 'string') {
      try {
        const parsed = JSON.parse(replies);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // 初回クイックリプライを表示するかどうかの判定
  const quickReplies = parseQuickReplies(chatState.selectedAgent?.quick_replies);
  const shouldShowInitialQuickReplies = chatState.messages.length === 0 && quickReplies.length > 0;

  // 表示するサジェストを決定（初回時のクイックリプライのみ表示、チャット後のサジェストは表示しない）
  const displaySuggestions = shouldShowInitialQuickReplies ? quickReplies : [];
  const currentChatTitle = chatState.isNewChat
    ? '新規チャット'
    : (() => {
        const chat = formattedChats.find(c => c.id === Number(chatId));
        return chat?.title || '無題のチャット';
      })();
  const renderSidebar = (
    selectedAgentId: number | null,
    onSelectAgent: (agentId: number) => void,
    mobile = false,
  ) => (
    <Sidebar
      onLogout={chatActions.handleLogout}
      chats={formattedChats}
      agents={sidebarAgents}
      selectedAgentId={selectedAgentId}
      onSelectAgent={onSelectAgent}
      onToggleCollapse={mobile ? () => undefined : chatState.setSidebarCollapsed}
      collapsed={mobile ? false : chatState.sidebarCollapsed}
    />
  );
  const agentSelectControl = (
    <Select
      value={chatState.selectedAgent?.id?.toString() || ''}
      onValueChange={(value) => {
        const agent = chatState.agents.find(a => a.id?.toString() === value);
        if (agent) {
          chatState.setSelectedAgent(agent);
        }
      }}
    >
      <SelectTrigger className="h-9 w-full min-w-0 border-0 bg-transparent text-sm font-medium hover:bg-gray-50 focus:ring-0 focus:ring-offset-0 sm:w-auto sm:min-w-[200px]">
        <div className="flex min-w-0 items-center space-x-2">
          <div className="h-2 w-2 shrink-0 rounded-full bg-green-500"></div>
          <span className="truncate">{chatState.selectedAgent?.title || chatState.selectedAgent?.name || 'エージェントを選択'}</span>
        </div>
      </SelectTrigger>
      <SelectContent align="start" className="w-[min(20rem,calc(100vw-2rem))]">
        {chatState.agents
          .filter((agent) => {
            // 対応するエージェント名を定義（優先的にマッチさせるタイトル）
            const preferredTitleMap: { [key: string]: string } = {
              'gpt-6-astra': 'GPT-6 Astra',
              'gpt-6-sol': 'GPT-6 Sol',
              'gpt-6-luna': 'GPT-6 Luna',
              'claude-fable-5.1': 'Claude Fable 5.1',
              'claude-opus-5.5': 'Claude Opus 5.5',
              'claude-sonnet-5': 'Claude Sonnet 5',
              'gpt-5': 'GPT-5',
              'gpt-5-mini': 'GPT-5 Mini',
              'gpt-5-nano': 'GPT-5 Nano',
              'o3': 'o3',
              'chatgpt-4o-mini': '4o mini-high',
              'claude-sonnet-4': 'Claude Sonnet 4',
              'claude-opus-4': 'Claude Opus 4',
              'gemini-2.5-flash': 'Gemini 2.5 Flash',
              'gemini-2.5-pro': 'Gemini 2.5 Pro',
              'bytedance-seed-1.6': 'ByteDance Seed 1.6',
              'bytedance-seed-1.6-flash': 'ByteDance Seed 1.6 Flash'
            };

            // model_typeとtitleが完全一致するもののみを表示
            const expectedTitle = preferredTitleMap[agent.model_type || ''];
            return expectedTitle && agent.title === expectedTitle;
          })
          .map((agent) => (
            <SelectItem key={agent.id} value={agent.id?.toString() || ''} className="p-3">
              <div className="flex min-w-0 flex-col space-y-1">
                <div className="flex min-w-0 items-center space-x-2">
                  <div className="h-2 w-2 shrink-0 rounded-full bg-green-500"></div>
                  <span className="truncate text-sm font-medium">{agent.title || agent.name}</span>
                </div>
                <span className="ml-4 truncate text-xs text-gray-500">{agent.description || 'AIエージェント'}</span>
              </div>
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );

  // Super Agentモード時は専用のUIを表示
  if (isSuperAgentMode) {
    return (
      <ResponsiveAppShell
        sidebar={renderSidebar(null, () => {})}
        mobileSidebar={renderSidebar(null, () => {}, true)}
        topbarTitle="Super Agent"
      >
        <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white lg:h-screen lg:min-h-0">
          <TaskyTopBar
            title="🤖 Super Agent"
            sidebarCollapsed={chatState.sidebarCollapsed}
            onExpandSidebar={() => chatState.setSidebarCollapsed(false)}
          />

          <SuperAgentChat
            userId={user?.id || 0}
            onError={(error) => {
              console.error('Super Agent Error:', error);
              // エラーハンドリング（必要に応じてトーストなどを表示）
            }}
          />
        </div>
      </ResponsiveAppShell>
    );
  }

  return (
    <ResponsiveAppShell
      sidebar={renderSidebar(chatState.selectedAgent?.id || null, handleChangeAgent)}
      mobileSidebar={renderSidebar(chatState.selectedAgent?.id || null, handleChangeAgent, true)}
      topbarTitle={currentChatTitle}
    >
      <OnboardingModal
        isOpen={showOnboarding}
        userName={(user?.name && user.name !== '未設定' && user.name !== '名称未設定') ? user.name : (user?.email ? user.email.split('@')[0] : 'User')}
        onComplete={handleOnboardingComplete}
        onClose={handleOnboardingClose}
      />

      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white lg:h-screen lg:min-h-0">
        <TaskyTopBar
          title={currentChatTitle}
          leading={<div className="flex min-w-0 items-center">{agentSelectControl}</div>}
          sidebarCollapsed={chatState.sidebarCollapsed}
          onExpandSidebar={() => chatState.setSidebarCollapsed(false)}
        />

        <div className="border-b border-warm-border bg-white px-3 py-2 lg:hidden">
          {agentSelectControl}
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          {/* 選択された商品のバナー */}
          {selectedProduct && (
            <SelectedProductBanner
              product={selectedProduct}
              onRemove={() => appDispatch(clearSelectedProduct())}
            />
          )}

          {/* 新規チャット時のウェルカム画面 + 入力エリア（中央配置） */}
          {shouldShowSelectedAgentWelcome ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 bg-white">
              <div className="max-w-4xl w-full">
                {/* ウェルカムメッセージ（/chat/aiの場合は常にこのメッセージ） */}
                <h2 className="text-3xl font-semibold text-center mb-12 text-dark">
                  こんにちは、何のお手伝いをしましょう？
                </h2>

                {/* 入力エリア（ウェルカム画面内） */}
                <NewChatInput
                  messageInput={chatState.messageInput}
                  setMessageInput={chatState.setMessageInput}
                  isLoading={chatState.isLoading}
                  onSendMessage={async (attachments?: File[], uploadedFiles?: any[], productId?: number, generationMode?: ChatGenerationMode) => {
                    await chatActions.handleSendMessageAsync(attachments, uploadedFiles, false, productId, undefined, undefined, undefined, generationMode, undefined, buildProductFocusPayload(selectedProduct, productId));
                  }}
                  selectedAgent={chatState.selectedAgent}
                  disabled={chatState.isLoading || chatState.showThinkingProcess}
                  isStickyDisabled={true}
                />
                <p className="text-xs text-center text-gray-400 mt-3">
                  AIは間違えることがあります。重要な情報は必ずご確認ください。
                </p>
              </div>
            </div>
          ) : (
            /* 通常のチャットメッセージ表示 */
            <>
              {/* エージェント選択時でメッセージがない場合、画面中央にエージェント情報を表示 */}
              {chatState.selectedAgent && chatState.messages.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center px-6">
                  <div className="text-center animate-fade-in-up">
                    <h2 className="text-3xl font-semibold text-dark mb-4">
                      {chatState.selectedAgent.title || chatState.selectedAgent.name}
                    </h2>
                    <p className="text-gray-500 text-lg max-w-xl mx-auto">
                      {chatState.selectedAgent.description || 'AIエージェントが会話をサポートします。'}
                    </p>
                  </div>
                </div>
              ) : (
                <NewChatMessages
                key={`messages-${chatId}-${chatState.isNewChat}-${chatState.selectedAgent?.id}`}
                messages={chatState.messages}
                isLoading={chatState.isLoading || chatState.typingIndicator}
                selectedAgent={chatState.selectedAgent}
                // エージェント未選択の新規チャット時は空のプロンプトを表示させるため isNewChat を渡す
                isNewChat={chatState.isNewChat && !chatState.selectedAgent && !isAiChatMode}
                chatId={chatId}
                chats={formattedChats}
                showThinkingProcess={chatState.showThinkingProcess}
                thinkingMessage={chatState.thinkingMessage}
                onThinkingProcessClose={() => {
                  chatState.setShowThinkingProcess(false);
                  chatState.setThinkingMessage('');
                }}
                onRegenerateMessage={handleRegenerateMessage}
                isRegenerating={chatState.isRegenerating}
                regeneratingMessageId={chatState.regeneratingMessageId}
                aiThinkingContent={chatState.aiThinkingContent}
                onToolAction={(text) => { void chatActions.sendTextMessage(text); }}
                toolActionsDisabled={chatState.isLoading || chatState.showThinkingProcess}
              />
              )}

              {/* チャット入力エリア（通常時） */}
              <div className="">
                {/* サジェスト機能 - AIモデル選択プルダウンの上に表示 */}
                <ChatSuggestions
                  suggestions={displaySuggestions}
                  onSuggestionClick={(suggestion) => {
                    chatState.setMessageInput(suggestion);
                    chatActions.handleSendMessageAsync();
                  }}
                  visible={displaySuggestions.length > 0 && !chatState.isLoading}
                  isInitialQuickReplies={shouldShowInitialQuickReplies}
                />

                {/* AIチャットモード時のモデル選択プルダウン（左下） */}
                {isAiChatMode && (
                  <div className="absolute bottom-full left-0 z-10 mb-2 flex items-center gap-2 rounded-md border border-[#E9ECEF] bg-white px-3 py-2 shadow-[0_2px_8px_rgba(0,0,0,0.1)]">
                    <label htmlFor="ai-model-select" className="whitespace-nowrap text-xs font-medium text-[#495057]">AIモデル:</label>
                    <div className="min-w-[90px]">
                      <TaskySelect
                        id="ai-model-select"
                        data-tutorial="chat-model-select"
                        value={selectedAiModel}
                        onChange={(e) => handleAiModelChange(e.target.value)}
                        className="h-8 text-xs"
                      >
                        <option value="gpt-6-astra">GPT-6 Astra</option>
                        <option value="gpt-6-sol">GPT-6 Sol</option>
                        <option value="gpt-6-luna">GPT-6 Luna</option>
                        <option value="gpt-5">GPT-5</option>
                      </TaskySelect>
                    </div>
                  </div>
                )}

                <NewChatInput
                  messageInput={chatState.messageInput}
                  setMessageInput={chatState.setMessageInput}
                  isLoading={chatState.isLoading}
                  onSendMessage={async (attachments?: File[], uploadedFiles?: any[], productId?: number, generationMode?: ChatGenerationMode) => {
                    await chatActions.handleSendMessageAsync(attachments, uploadedFiles, false, productId, undefined, undefined, undefined, generationMode, undefined, buildProductFocusPayload(selectedProduct, productId));
                  }}
                  selectedAgent={chatState.selectedAgent}
                  disabled={chatState.isLoading || chatState.showThinkingProcess}
                  isStickyDisabled={shouldShowAgentCards && !isAiChatMode}
                />
                <p className="text-xs text-center text-gray-400 mt-2 mb-3 px-6">
                  AIは間違えることがあります。重要な情報は必ずご確認ください。
                </p>
              </div>

              {/* AIチャットモード時はエージェントカードを表示しない */}
              {shouldShowAgentCards && !isAiChatMode && (
                <div className="p-5">
                  <h2 className="mb-5 text-center text-2xl text-[#333333]">AIエージェントを選択してください</h2>
                  <div className="mx-auto grid max-w-[860px] grid-cols-3 gap-5 pb-5">
                    {formattedAgents
                      .filter(agent => {
                        return agent.is_visible_in_sidebar !== false;
                      })
                      .map(agent => (
                        <AgentCard
                          key={agent.id}
                          agent={agent}
                          onClick={() => handleChangeAgent(agent.id)}
                        />
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 既存のAgentSelectorは、カードリスト表示時は表示しない */}
        {chatState.agentSelectorVisible && !shouldShowAgentCards && (
          <div className="absolute right-5 top-2.5 z-20 rounded-lg bg-white shadow-[0_4px_12px_rgba(0,0,0,0.15)]">
            <AgentSelector
              agents={formattedAgents}
              selectedAgentId={chatState.selectedAgent ? chatState.selectedAgent.id : null}
              onSelectAgent={handleChangeAgent}
            />
          </div>
        )}
      </div>
    </ResponsiveAppShell>
  );
};

export default ChatPage;
